// This file is a migration bridge for callsites that are guaranteed to be in a TanStack Router context,
// but render components that implement React-Router-style APIs.
//
// Consistent export names across `@github-ui/react-router` exports are enforced by `exports.browser.test.ts`.

// eslint-disable-next-line @github-ui/github-monorepo/prefer-github-ui-react-router
import {
  matchPath as reactRouterMatchPath,
  type PathMatch as ReactRouterPathMatch,
  type PathPattern as ReactRouterPathPattern,
} from 'react-router'
import {useLocation} from './tanstack-router'

// TanStack-only implementations that mirror React Router APIs.
export * from './tanstack-router'
export {DEFAULT_INTENT_PRELOAD_DELAY, type PreloadableRoute, type PreloadProps} from './shared'

// Re-exports remaining React Router APIs that haven't been implemented in TanStack Router yet.
// These exports should be removed as TanStack Router equivalent APIs are added.
export type {
  Blocker,
  BrowserHistory,
  ErrorResponse,
  LinkProps,
  LoaderFunction,
  Location,
  GetScrollRestorationKeyFunction,
  HistoryRouterProps,
  InitialEntry,
  MemoryRouterProps,
  NavigateProps,
  NavLinkProps,
  Params,
  PathParam,
  RelativeRoutingType,
  RouteObject,
  RouterProviderProps,
  RoutesContextType,
  SetURLSearchParams,
  ShouldRevalidateFunction,
  ShouldRevalidateFunctionArgs,
  UIMatch,
} from './react-router'

export {
  BrowserRouter,
  createBrowserRouter,
  createMemoryRouter,
  createPath,
  createSearchParams,
  createStaticHandler,
  createStaticRouter,
  generatePath,
  isRouteErrorResponse,
  matchPath,
  matchRoutes,
  MemoryRouter,
  NavigationType,
  Navigate,
  resolvePath,
  Route,
  Router,
  RouterProvider,
  Routes,
  RoutesContext,
  ScrollRestoration,
  StaticRouter,
  StaticRouterProvider,
  UNSAFE_createBrowserHistory,
  unstable_usePrompt,
  useBeforeUnload,
  useDangerousNavigate,
  useNavigationType,
  useResolvedPath,
  useRouteError,
  useRoutes,
  useDangerousSearchParams,
  useInRouterContext,
} from './react-router'

/**
 * We export this here and not from `./tanstack-router/index` because the shim relies
 * on React Router's matchPath utility. ./tanstack-router/* exports should be TanStack-only.
 * Reimplementating the internals of matchPath is non-trivial and not currently worth the maintenance burden.
 */
export function useMatch<ParamKey extends string = string>(
  pattern: string | ReactRouterPathPattern,
): ReactRouterPathMatch<ParamKey> | null {
  const location = useLocation() // location from TSR context
  return reactRouterMatchPath(pattern, location.pathname) // react router utility, does not depend on RR context.
}
